import React, { useRef, useEffect, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Sphere } from '@react-three/drei';
import * as THREE from 'three';

interface Earth3DProps {
  position?: [number, number, number];
  scale?: number;
}

const Earth3D: React.FC<Earth3DProps> = ({
  position = [-200, 50, -100],
  scale = 25
}) => {
  const earthRef = useRef<THREE.Mesh>(null);
  const [earthTexture, setEarthTexture] = useState<THREE.Texture | null>(null);
  const [cloudsTexture, setCloudsTexture] = useState<THREE.Texture | null>(null);
  const [normalMap, setNormalMap] = useState<THREE.Texture | null>(null);
  const [specularMap, setSpecularMap] = useState<THREE.Texture | null>(null);
  const cloudsRef = useRef<THREE.Mesh>(null);

  // Carregar texturas manualmente para evitar problemas
  useEffect(() => {
    const textureLoader = new THREE.TextureLoader();

    // Textura principal da Terra
    textureLoader.load(
      'https://threejs.org/examples/textures/planets/earth_atmos_2048.jpg',
      (texture) => {
        setEarthTexture(texture);
      }
    );

    // Textura de nuvens
    textureLoader.load(
      'https://threejs.org/examples/textures/planets/earth_clouds_1024.png',
      (texture) => {
        setCloudsTexture(texture);
      }
    );

    // Normal map para relevo
    textureLoader.load(
      'https://threejs.org/examples/textures/planets/earth_normal_2048.jpg',
      (texture) => {
        setNormalMap(texture);
      }
    );

    // Mapa especular
    textureLoader.load(
      'https://threejs.org/examples/textures/planets/earth_specular_2048.jpg',
      (texture) => {
        setSpecularMap(texture);
      }
    );
  }, []);

  // Animação de rotação suave
  useFrame(({ clock }) => {
    if (earthRef.current) {
      // Rotação lenta da Terra
      earthRef.current.rotation.y = clock.getElapsedTime() * 0.05;
    }

    if (cloudsRef.current) {
      // Rotação um pouco mais rápida das nuvens
      cloudsRef.current.rotation.y = clock.getElapsedTime() * 0.07;
    }
  });

  return (
    <group position={position} rotation={[0, 0, 0]}>
      {/* Luzes para iluminar a Terra - intensidade aumentada para destacar águas */}
      <pointLight position={[100, 100, 100]} intensity={4.0} color="#ffffff" />
      <pointLight position={[-50, 50, 50]} intensity={3.0} color="#f0f8ff" />
      <ambientLight intensity={1.0} color="#ffffff" />

      {/* Planeta Terra com texturas realistas */}
      <Sphere ref={earthRef} args={[scale, 64, 64]}>
        {earthTexture ? (
          <meshPhongMaterial
            map={earthTexture}
            normalMap={normalMap || undefined}
            specularMap={specularMap || undefined}
            specular={new THREE.Color(0x333333)}
            shininess={15}
          />
        ) : (
          <meshPhongMaterial
            color="#1E88E5"
            shininess={15}
          />
        )}
      </Sphere>

      {/* Camada de nuvens */}
      {cloudsTexture && (
        <Sphere ref={cloudsRef} args={[scale * 1.01, 64, 64]}>
          <meshPhongMaterial
            map={cloudsTexture}
            transparent={true}
            opacity={0.4}
            depthWrite={false}
          />
        </Sphere>
      )}
    </group>
  );
};

export default Earth3D;
